import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as QRCode from 'qrcode';
import { CartaoCriptografado, CartaoDigitado, LinkCheckout, PagamentoAdesao } from '../../../../interfaces/adesao.dto';
import { environment } from '../../../../../environments/environment';
import {
  SerialEmCache,
  TerminalAdyen,
  filtrarTerminaisPorTipo,
  lembrarSerial,
  lerCacheSeriais,
  limparCacheSeriais,
  modeloPorSerial,
  montarTerminalFull,
  poiidPorSerial,
  serialInicialPorTipo,
} from '../../../../utils/maquineta.util';
import { BrlPipe } from '../../../../utils/brl.pipe';

/** O que muda entre a cobrança da adesão e a da regularização: quem é a cobrança. */
export interface AcoesCobranca {
  cobrarNoTerminal(poiid: string): Promise<void>;
  /** Devolve a url para o QR. */
  gerarLink(): Promise<PagamentoAdesao>;
  enviarLink(url: string, email: boolean, whatsApp: boolean): Promise<PagamentoAdesao>;
  /** Consultado pelo laço de 5s e pelo "Verificar pagamento". */
  foiPago(): Promise<boolean>;
  /** Link do checkout (cartão de crédito); com canal, o back também envia. Sem a função, a opção não aparece. */
  gerarCheckout?(canal: CanalCheckout | null, destino?: string): Promise<LinkCheckout>;
  /** Cartão digitado pelo operador; a aprovação chega pelo `foiPago`. Sem a função, a aba não aparece. */
  pagarComCartao?(dados: CartaoDigitado): Promise<void>;
}

/** Mesmas bandeiras do admin do CartãoSiM. */
export const BANDEIRAS_ADYEN = [
  'amex', 'argencard', 'bcmc', 'bijcard', 'cabal', 'cartebancaire', 'codensa', 'cup', 'dankort', 'diners',
  'discover', 'electron', 'elo', 'forbrugsforeningen', 'hiper', 'hipercard', 'jcb', 'karenmillen', 'laser',
  'maestro', 'maestrouk', 'mc', 'mcalphabankbonus', 'mir', 'naranja', 'oasis', 'rupay', 'shopping', 'solo',
  'troy', 'uatp', 'visa', 'visaalphabankbonus', 'visadankort', 'warehouse',
];

/** As que o back converte em paymentCompanyCode (AdesaoCartao.Bandeiras); as demais ele recusa. */
export const BANDEIRAS_ACEITAS: { codigo: string; nome: string }[] = [
  { codigo: 'visa', nome: 'Visa' },
  { codigo: 'mc', nome: 'Mastercard' },
  { codigo: 'elo', nome: 'Elo' },
  { codigo: 'hipercard', nome: 'Hipercard' },
  { codigo: 'amex', nome: 'American Express' },
  { codigo: 'diners', nome: 'Diners Club' },
  { codigo: 'jcb', nome: 'JCB' },
];

export type CanalCheckout = 'email' | 'whatsapp';
export type MeioCobranca = 'maquininha' | 'pix' | 'link' | 'cartao';

/** Maquininha ou PIX/link, com espera do pagamento. Quem é cobrado vem nas `acoes`. */
@Component({
  selector: 'app-cobranca-sim',
  standalone: true,
  imports: [CommonModule, FormsModule, BrlPipe],
  templateUrl: './cobranca-sim.component.html',
  styleUrl: './cobranca-sim.component.scss',
})
export class CobrancaSimComponent implements OnInit, OnDestroy {
  @Input() acoes!: AcoesCobranca;
  @Input() valor: number | null = null;
  @Input() guiche = '';
  @Input() aceitaPix = true;
  @Input() podeEnviarLink = true;
  /** Já existe cobrança do outro lado: o "Verificar pagamento" aparece também na maquininha. */
  @Input() cobrancaCriada = false;
  /** Destinos do link do checkout, já formatados para conferência. */
  @Input() contatosCheckout: Record<CanalCheckout, string> = { email: '', whatsapp: '' };
  @Input() checkoutLiberado = true;
  @Input() rodape = 'O atendimento segue como particular até a cobrança ser aprovada.';
  /** O plano tem a forma cartão de crédito. */
  @Input() cartaoHabilitado = false;
  @Input() maxParcelas = 1;

  @Output() pago = new EventEmitter<void>();
  @Output() voltar = new EventEmitter<void>();
  @Output() semResposta = new EventEmitter<void>();

  meio: MeioCobranca = 'maquininha';
  serial = '';
  lembrar = true;
  cacheSeriais: SerialEmCache[] = [];
  statusCobranca: 'pronto' | 'aguardando' | 'falha' = 'pronto';
  pagamento: PagamentoAdesao | null = null;
  qrPix: string | null = null;
  avisosLink: string[] = [];
  isEnviandoLink = false;
  isProcessando = false;
  erro = '';
  verificandoPagamento = false;
  avisoPagamento = '';

  readonly MODELOS = ['V240mPlus', 'P400Plus', 'S1F2'];
  // Vazio = deduz pelo serial; o operador escolhe quando o serial foge do padrao.
  modeloEscolhido = '';

  // Sem pagamento nesse tempo, a maquininha "não respondeu": a Fila não recebe o negado da Adyen.
  private readonly ESPERA_MAQUININHA_MS = 3 * 60 * 1000;
  readonly INTERVALO_VERIFICACAO_S = 5;
  private ultimaVerificacao: number | null = null;
  private laco: any = null;
  private lacoFim = 0;

  /** Segundos desde a última consulta (automática ou manual); null antes da primeira. */
  get segundosDesdeVerificacao(): number | null {
    return this.ultimaVerificacao === null ? null : Math.max(0, Math.round((Date.now() - this.ultimaVerificacao) / 1000));
  }

  ngOnInit(): void {
    this.cacheSeriais = lerCacheSeriais(this.guiche);
    if (!this.serial) this.serial = this.cacheSeriais[0]?.serial ?? '';
  }

  ngOnDestroy(): void {
    this.pararLaco();
    this.desmontarCartao();
  }

  // ── Abas ────────────────────────────────────────────────────

  get abas(): MeioCobranca[] {
    const abas: MeioCobranca[] = ['maquininha', 'pix'];
    if (this.aceitaCheckout) abas.push('link');
    if (this.aceitaCartao) abas.push('cartao');
    return abas;
  }

  abaBloqueada(aba: MeioCobranca): boolean {
    if (this.statusCobranca === 'aguardando' || this.aguardandoCartao) return true;
    if (aba === 'pix') return !this.aceitaPix;
    if (aba === 'maquininha') return !!this.qrPix;
    return false;
  }

  escolherMeio(aba: MeioCobranca): void {
    if (this.abaBloqueada(aba) || aba === this.meio) return;
    if (this.meio === 'cartao') this.desmontarCartao();
    this.meio = aba;
    // O container dos secured fields so existe depois que o painel renderiza.
    if (aba === 'cartao') setTimeout(() => this.montarCartao(this.containerCartao?.nativeElement));
  }

  // ── Maquininha ──────────────────────────────────────────────

  get modeloSelecionado(): string {
    return this.modeloEscolhido || modeloPorSerial(this.serial);
  }

  get poiid(): string {
    const modelo = this.modeloSelecionado;
    return modelo ? montarTerminalFull(modelo, this.serial) : poiidPorSerial(this.serial);
  }

  escolherModelo(modelo: string): void {
    this.modeloEscolhido = modelo;
    if (!this.serial) this.serial = serialInicialPorTipo(modelo);
  }

  // Maquininhas cadastradas do modelo, para o operador tocar em vez de digitar.
  get sugestoesTerminal(): TerminalAdyen[] {
    const modelo = this.modeloSelecionado;
    if (!modelo) return [];
    return filtrarTerminaisPorTipo(modelo, this.serial === serialInicialPorTipo(modelo) ? '' : this.serial, 6)
      .filter(t => t.numero !== this.serial);
  }

  usarSerial(serial: string, modelo = ''): void {
    this.serial = serial;
    this.modeloEscolhido = modelo;
  }

  /** Serial comprido que não bate com nenhum modelo: borda âmbar. */
  get serialSuspeito(): boolean {
    return this.serial.length >= 15 && !this.poiid;
  }

  onSerial(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.serial = input.value.replace(/\D/g, '').slice(0, 20);
    input.value = this.serial;
  }

  limparCache(): void {
    limparCacheSeriais(this.guiche);
    this.cacheSeriais = [];
  }

  async cobrarNaMaquininha(): Promise<void> {
    if (!this.poiid || this.isProcessando) return;
    this.isProcessando = true;
    this.erro = '';
    try {
      if (this.lembrar) this.cacheSeriais = lembrarSerial(this.guiche, this.serial, this.modeloEscolhido);
      await this.acoes.cobrarNoTerminal(this.poiid);
      this.statusCobranca = 'aguardando';
      this.iniciarLaco(this.ESPERA_MAQUININHA_MS, () => {
        this.statusCobranca = 'falha';
        this.semResposta.emit();
      });
    } catch (e: any) {
      this.erro = e?.message ?? 'A maquininha não recebeu a cobrança.';
      this.statusCobranca = 'falha';
    } finally {
      this.isProcessando = false;
    }
  }

  cancelarEspera(): void {
    this.pararLaco();
    this.statusCobranca = 'pronto';
  }

  // ── PIX / link ──────────────────────────────────────────────

  async gerarPix(): Promise<void> {
    if (this.isProcessando) return;
    this.isProcessando = true;
    this.erro = '';
    try {
      this.pagamento = await this.acoes.gerarLink();
      this.qrPix = await QRCode.toDataURL(this.pagamento.url, { width: 200, margin: 1 });
      this.iniciarLaco(10 * 60 * 1000, () => undefined);
    } catch (e: any) {
      this.erro = e?.message ?? 'Não foi possível gerar o PIX.';
    } finally {
      this.isProcessando = false;
    }
  }

  async enviarLink(email: boolean, whatsApp: boolean): Promise<void> {
    if (!this.pagamento?.url || !this.podeEnviarLink || this.isEnviandoLink) return;
    this.isEnviandoLink = true;
    this.erro = '';
    try {
      const envio = await this.acoes.enviarLink(this.pagamento.url, email, whatsApp);
      this.avisosLink = envio.avisos ?? [];
    } catch (e: any) {
      this.erro = e?.message ?? 'Não foi possível enviar o link.';
    } finally {
      this.isEnviandoLink = false;
    }
  }

  // ── Cartão de crédito (link do checkout) ────────────────────

  linkCheckout: string | null = null;
  qrCheckout: string | null = null;
  avisoCheckout = '';
  isGerandoCheckout = false;

  get aceitaCheckout(): boolean {
    return !!this.acoes?.gerarCheckout;
  }

  readonly canaisCheckout: CanalCheckout[] = ['whatsapp', 'email'];

  // Contato corrigido pelo operador vale só para este envio; o cadastro do CartãoSiM não muda.
  contatoCheckoutEditado: Partial<Record<CanalCheckout, string>> = {};
  editandoCheckout: CanalCheckout | null = null;
  valorEdicaoCheckout = '';
  erroEdicaoCheckout = '';

  destinoCheckout(canal: CanalCheckout): string {
    const editado = this.contatoCheckoutEditado[canal];
    if (!editado) return this.contatosCheckout[canal];
    return canal === 'whatsapp' ? `(${editado.slice(0, 2)}) ${editado.slice(2, 7)}-${editado.slice(7)}` : editado;
  }

  editarContatoCheckout(canal: CanalCheckout): void {
    this.editandoCheckout = canal;
    const atual = this.contatoCheckoutEditado[canal] ?? this.contatosCheckout[canal] ?? '';
    this.valorEdicaoCheckout = canal === 'whatsapp' ? atual.replace(/\D/g, '').replace(/^55(?=\d{11}$)/, '') : atual;
    this.erroEdicaoCheckout = '';
  }

  salvarContatoCheckout(): void {
    const canal = this.editandoCheckout;
    if (!canal) return;
    const valor = this.valorEdicaoCheckout.trim();
    if (canal === 'whatsapp') {
      const d = valor.replace(/\D/g, '');
      if (d.length !== 11 || d[2] !== '9') {
        this.erroEdicaoCheckout = 'Celular com DDD: 11 dígitos, começando com 9 depois do DDD.';
        return;
      }
      this.contatoCheckoutEditado.whatsapp = d;
    } else {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(valor)) {
        this.erroEdicaoCheckout = 'E-mail inválido.';
        return;
      }
      this.contatoCheckoutEditado.email = valor.toLowerCase();
    }
    this.editandoCheckout = null;
  }

  cancelarContatoCheckout(): void {
    this.editandoCheckout = null;
    this.erroEdicaoCheckout = '';
  }

  async enviarCheckout(canal: CanalCheckout | null): Promise<void> {
    if (!this.acoes.gerarCheckout || !this.checkoutLiberado || this.isGerandoCheckout) return;
    this.isGerandoCheckout = true;
    this.erro = '';
    this.avisoCheckout = '';
    try {
      const { link } = await this.acoes.gerarCheckout(canal, canal ? this.contatoCheckoutEditado[canal] : undefined);
      this.linkCheckout = link;
      if (canal) {
        const para = this.destinoCheckout(canal);
        this.avisoCheckout = `Link enviado${para ? ' para ' + para : ''}. A assinatura ativa quando o paciente pagar no checkout.`;
      } else {
        this.avisoCheckout = await this.copiar(link)
          ? 'Link copiado. A assinatura ativa quando o paciente pagar no checkout.'
          : 'Não foi possível copiar: selecione o link abaixo. A assinatura ativa quando o paciente pagar no checkout.';
      }
      this.qrCheckout = await QRCode.toDataURL(link, { width: 200, margin: 1 }).catch(() => null);
      this.iniciarLaco(15 * 60 * 1000, () => undefined);
    } catch (e: any) {
      this.erro = e?.message ?? 'Não foi possível gerar o link do checkout.';
    } finally {
      this.isGerandoCheckout = false;
    }
  }

  // Com o link ja gerado, copia o mesmo: gerar de novo criaria outra cobranca no checkout.
  async copiarLinkCheckout(): Promise<void> {
    if (!this.linkCheckout) {
      await this.enviarCheckout(null);
      return;
    }
    this.avisoCopia = await this.copiar(this.linkCheckout) ? 'Link copiado.' : 'Não foi possível copiar: selecione o link.';
  }

  avisoCopia = '';

  // Clipboard so existe em https/localhost; fora disso o link fica visivel para copiar na mao.
  private async copiar(texto: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(texto);
      return true;
    } catch {
      return false;
    }
  }

  // ── Digitar cartão (secured fields da Adyen) ────────────────

  @ViewChild('cartaoContainer') containerCartao?: ElementRef<HTMLElement>;

  // Instancia, nao readonly: os testes trocam a chave e a fabrica (a Adyen nao monta no Karma).
  adyenClientKey: string = (environment as any).adyenClientKey ?? '';
  adyenEnvironment: string = (environment as any).adyenEnvironment ?? 'live';
  criarCheckoutAdyen: (config: any) => Promise<any> = async config => {
    const { default: AdyenCheckout } = await import('@adyen/adyen-web');
    return AdyenCheckout(config);
  };

  nomeCartao = '';
  parcelasCartao = 1;
  cartaoValido = false;
  bandeiraCartao = '';
  /** Escolhida pela Adyen a partir do número; false quando o operador escolheu ou falta escolher. */
  bandeiraIdentificada = false;
  readonly bandeirasAceitas = BANDEIRAS_ACEITAS;
  carregandoCartao = false;
  isPagandoCartao = false;
  aguardandoCartao = false;
  cartaoEnviado = false;
  avisoCartao = '';
  private dadosCartao: (CartaoCriptografado & { checkoutAttemptId?: string }) | null = null;
  private camposCartao: any = null;

  get aceitaCartao(): boolean {
    return !!this.acoes?.pagarComCartao && this.cartaoHabilitado && !!this.adyenClientKey;
  }

  get opcoesParcelas(): { n: number; valor: number }[] {
    const max = Math.max(1, Math.floor(this.maxParcelas || 1));
    const total = this.valor ?? 0;
    return Array.from({ length: max }, (_, i) => ({ n: i + 1, valor: Math.round((total / (i + 1)) * 100) / 100 }));
  }

  get podePagarCartao(): boolean {
    return this.cartaoValido && !!this.dadosCartao && !!this.nomeCartao.trim() && !!this.bandeiraCartao
      && this.checkoutLiberado && !this.isPagandoCartao && !this.aguardandoCartao;
  }

  /** Bandeira que o back não aceita (ou 'card', quando a Adyen não reconhece) fica para o operador escolher. */
  identificarBandeira(brand?: string): void {
    const aceita = BANDEIRAS_ACEITAS.some(b => b.codigo === brand);
    this.bandeiraCartao = aceita ? brand! : '';
    this.bandeiraIdentificada = aceita;
  }

  escolherBandeira(codigo: string): void {
    this.bandeiraCartao = codigo;
    this.bandeiraIdentificada = false;
  }

  async montarCartao(container?: HTMLElement): Promise<void> {
    if (!container || this.camposCartao || this.carregandoCartao) return;
    this.carregandoCartao = true;
    this.erro = '';
    try {
      const checkout = await this.criarCheckoutAdyen({
        locale: 'pt-BR',
        environment: this.adyenEnvironment,
        clientKey: this.adyenClientKey,
        onChange: (state: any) => this.onCartaoChange(state),
      });
      // No AdyenCheckout o onBrand nunca dispara: a bandeira ia vazia e o back recusava o cartao.
      this.camposCartao = checkout.create('securedfields', {
        onBrand: (state: any) => this.identificarBandeira(state?.brand),
        brands: BANDEIRAS_ADYEN,
        styles: {
          base: { color: '#24292D', fontSize: '14px', fontFamily: 'Nunito, system-ui, sans-serif' },
          error: { color: '#B4003C' },
          validated: { color: '#0E7A53' },
          placeholder: { color: '#8A8494' },
        },
      }).mount(container);
    } catch (e: any) {
      this.erro = e?.message ?? 'Não foi possível carregar os campos do cartão.';
    } finally {
      this.carregandoCartao = false;
    }
  }

  onCartaoChange(state: any): void {
    const pm = state?.data?.paymentMethod ?? {};
    this.cartaoValido = !!state?.isValid;
    this.dadosCartao = this.cartaoValido
      ? {
          encryptedCardNumber: pm.encryptedCardNumber, encryptedExpiryMonth: pm.encryptedExpiryMonth,
          encryptedExpiryYear: pm.encryptedExpiryYear, encryptedSecurityCode: pm.encryptedSecurityCode,
          checkoutAttemptId: pm.checkoutAttemptId,
        }
      : null;
    if (pm.brand) this.identificarBandeira(pm.brand);
  }

  desmontarCartao(): void {
    try {
      this.camposCartao?.unmount?.();
    } catch {
      // Container ja saiu do DOM: nada a desfazer.
    }
    this.camposCartao = null;
    this.cartaoValido = false;
    this.dadosCartao = null;
    this.bandeiraCartao = '';
    this.bandeiraIdentificada = false;
  }

  async pagarCartao(): Promise<void> {
    if (!this.acoes.pagarComCartao || !this.podePagarCartao || !this.dadosCartao) return;
    this.isPagandoCartao = true;
    this.erro = '';
    this.avisoCartao = '';
    try {
      const { checkoutAttemptId, ...encrypted } = this.dadosCartao;
      await this.acoes.pagarComCartao({
        holderName: this.nomeCartao.trim(),
        brand: this.bandeiraCartao,
        parcelas: this.parcelasCartao,
        encrypted,
        checkoutAttemptId,
      });
      this.aguardandoCartao = true;
      this.cartaoEnviado = true;
      this.avisoCartao = 'Pagamento enviado. Aguardando a aprovação do cartão.';
      this.iniciarLaco(this.ESPERA_MAQUININHA_MS, () => {
        this.aguardandoCartao = false;
        this.avisoCartao = '';
        this.erro = 'O cartão ainda não foi aprovado. Use "Verificar pagamento" ou tente outra forma.';
      });
    } catch (e: any) {
      this.erro = e?.message ?? 'Não foi possível pagar com o cartão.';
    } finally {
      this.isPagandoCartao = false;
    }
  }

  // ── Espera do pagamento ─────────────────────────────────────

  private iniciarLaco(duracaoMs: number, aoEsgotar: () => void): void {
    this.pararLaco();
    this.lacoFim = Date.now() + duracaoMs;
    this.laco = setInterval(async () => {
      if (Date.now() > this.lacoFim) {
        this.pararLaco();
        aoEsgotar();
        return;
      }
      this.ultimaVerificacao = Date.now();
      if (await this.acoes.foiPago()) {
        this.pararLaco();
        this.pago.emit();
      }
    }, this.INTERVALO_VERIFICACAO_S * 1000);
  }

  private pararLaco(): void {
    if (this.laco) clearInterval(this.laco);
    this.laco = null;
  }

  // Manual (botao) responde na tela; o laco automatico so age quando confirma.
  async verificarPagamento(): Promise<void> {
    if (this.verificandoPagamento) return;
    this.verificandoPagamento = true;
    this.avisoPagamento = '';
    this.ultimaVerificacao = Date.now();
    try {
      if (await this.acoes.foiPago()) {
        this.pararLaco();
        this.pago.emit();
      } else {
        this.avisoPagamento = 'Pagamento ainda não confirmado. Aguarde alguns segundos e verifique de novo.';
      }
    } catch {
      this.avisoPagamento = 'Não foi possível consultar o pagamento agora.';
    } finally {
      this.verificandoPagamento = false;
    }
  }
}
